import {
  PUBLIC_DEPLOYMENT_CONFIGS,
  resolveDeploymentEnvironment,
} from "@sozo-ai/shared/deployment/public";

export type PlatformTarget = "local" | "development" | "production";
export type LocalMazricaTarget = "rc" | "local";

export type PublicPlatformConfig = Readonly<{
  target: PlatformTarget;
  appUrl: string;
  inquiryWebformUrl: string;
  mazrica: Readonly<{
    apiBaseUrl: string;
    oauthApiBaseUrl: string;
    webBaseUrl: string;
    idpIssuer: string;
    idpClientId: string;
  }>;
}>;

const SHARED_PUBLIC_CONFIG = Object.freeze({
  inquiryWebformUrl:
    "https://senses-form.mazrica.com/webforms/?webformid=GKqp2uZ2mxYL",
});

const DEVELOPMENT_MAZRICA_CONFIG = Object.freeze({
  apiBaseUrl: "https://senses-rc-open-api.mazrica.com/v1",
  oauthApiBaseUrl: "https://senses-rc-api.mazrica.com/v1",
  webBaseUrl: "https://senses-rc.mazrica.com",
  idpIssuer: "https://senses-rc-api.mazrica.com",
  idpClientId: "YWjLhxsC7dM_MD9MkgEHPHV4AJzzzCNNnQw-zV_GhHQ",
});

const LOCAL_MAZRICA_CONFIG_BASE = Object.freeze({
  apiBaseUrl: "http://localhost:3000/v1",
  oauthApiBaseUrl: "http://localhost:3000/v1",
  webBaseUrl: "http://localhost:9000",
  idpIssuer: "http://localhost:3000",
});

const DEFAULT_LOCAL_APP_PORT = 3000;

export function resolveLocalMazricaTarget(
  value: unknown = process.env["MAZRICA_API_TARGET"],
): LocalMazricaTarget {
  if (value === undefined || value === "" || value === "rc") return "rc";
  if (value === "local") return "local";

  throw new Error(`Invalid MAZRICA_API_TARGET: ${String(value)}`);
}

function resolveLocalMazricaConfig(
  clientId: unknown = process.env["MAZRICA_LOCAL_IDP_CLIENT_ID"],
) {
  if (typeof clientId !== "string" || clientId.trim() === "") {
    throw new Error("MAZRICA_LOCAL_IDP_CLIENT_ID is required");
  }

  return Object.freeze({
    ...LOCAL_MAZRICA_CONFIG_BASE,
    idpClientId: clientId.trim(),
  });
}

export function resolveLocalAppUrl(
  port: unknown = process.env["PORT"],
): string {
  const normalizedPort =
    typeof port === "string" && /^\d+$/.test(port.trim())
      ? Number(port)
      : DEFAULT_LOCAL_APP_PORT;

  if (normalizedPort < 1 || normalizedPort > 65_535) {
    throw new Error(`Invalid local Sozo port: ${String(port)}`);
  }

  return `http://localhost:${normalizedPort}`;
}

const PRODUCTION_MAZRICA_CONFIG = Object.freeze({
  apiBaseUrl: "https://sense-api.mazrica.com/v1",
  oauthApiBaseUrl: "https://sense-api.mazrica.com/v1",
  webBaseUrl: "https://senses.mazrica.com",
  idpIssuer: "https://sense-api.mazrica.com",
  idpClientId: "Tx_iE4xzItl3hPrg_2CQdqlKCouR2VXYRCX3Yb0QDNo",
});

export const PLATFORM_CONFIGS = Object.freeze({
  local: Object.freeze({
    target: "local",
    appUrl: `http://localhost:${DEFAULT_LOCAL_APP_PORT}`,
    ...SHARED_PUBLIC_CONFIG,
    mazrica: DEVELOPMENT_MAZRICA_CONFIG,
  }),
  development: Object.freeze({
    target: "development",
    appUrl: PUBLIC_DEPLOYMENT_CONFIGS.development.appUrl,
    ...SHARED_PUBLIC_CONFIG,
    mazrica: DEVELOPMENT_MAZRICA_CONFIG,
  }),
  production: Object.freeze({
    target: "production",
    appUrl: PUBLIC_DEPLOYMENT_CONFIGS.production.appUrl,
    ...SHARED_PUBLIC_CONFIG,
    mazrica: PRODUCTION_MAZRICA_CONFIG,
  }),
}) satisfies Readonly<Record<PlatformTarget, PublicPlatformConfig>>;

function getCurrentVercelMarkers() {
  return {
    NEXT_PUBLIC_VERCEL_PROJECT_ID: process.env["NEXT_PUBLIC_VERCEL_PROJECT_ID"],
    VERCEL: process.env["VERCEL"],
    VERCEL_PROJECT_ID: process.env["VERCEL_PROJECT_ID"],
  };
}

export function resolvePlatformTarget(
  environment: Readonly<Record<string, unknown>> = getCurrentVercelMarkers(),
): PlatformTarget {
  const deploymentEnvironment = resolveDeploymentEnvironment(environment);
  if (deploymentEnvironment) return deploymentEnvironment;

  if (environment["VERCEL"] === "1") {
    throw new Error("Unable to resolve the Vercel deployment target");
  }

  return "local";
}

export function getPlatformConfig(
  target: PlatformTarget = resolvePlatformTarget(),
  localMazricaTarget: unknown = process.env["MAZRICA_API_TARGET"],
  localMazricaIdpClientId: unknown = process.env["MAZRICA_LOCAL_IDP_CLIENT_ID"],
): PublicPlatformConfig {
  const config = PLATFORM_CONFIGS[target];
  if (target !== "local") return config;

  return Object.freeze({
    ...config,
    appUrl: resolveLocalAppUrl(),
    mazrica:
      resolveLocalMazricaTarget(localMazricaTarget) === "local"
        ? resolveLocalMazricaConfig(localMazricaIdpClientId)
        : DEVELOPMENT_MAZRICA_CONFIG,
  });
}

/** Platform-wide constants that are identical across every target. */
export const APP_AWS_REGION = "ap-northeast-1";
export const APP_DATABASE_NAME = "aiagent";
export const APP_SERVICE_NAME = "mazrica-ai-agent-platform-web";

export const DEVELOPMENT_VERCEL_PROJECT_ID =
  PUBLIC_DEPLOYMENT_CONFIGS.development.vercel.projectId;
export const PRODUCTION_VERCEL_PROJECT_ID =
  PUBLIC_DEPLOYMENT_CONFIGS.production.vercel.projectId;
export const DEVELOPMENT_APP_URL = PUBLIC_DEPLOYMENT_CONFIGS.development.appUrl;
export const PRODUCTION_APP_URL = PUBLIC_DEPLOYMENT_CONFIGS.production.appUrl;

const RECALL_REGION = APP_AWS_REGION;
export const RECALL_API_BASE_URL = `https://${RECALL_REGION}.recall.ai`;
export const RECALL_MEDIA_CLEANUP_GRACE_HOURS = 24;
export const RECALL_MEDIA_CLEANUP_MAX_ATTEMPTS = 10;

export const MEETING_MEDIA_ARCHIVE_PREFIX = "meetings";
export const MEETING_MEDIA_PRESIGN_TTL_SECONDS = 60 * 60;

export const ELEVENLABS_TRANSCRIPTION_MODEL_ID = "scribe_v2";
export const ELEVENLABS_TRANSCRIPTION_LANGUAGE_CODE = "jpn";

export const CREDIT_PRICING_FALLBACK = Object.freeze({
  elevenLabsSttCostUsdPerHour: "0.220000000",
  elevenLabsSttKeytermPromptingCostUsdPerHour: "0.050000000",
  recallBotCostUsdPerHour: "0.500000000",
  usdToJpyRate: "160.000000000",
  llmMarkupFactor: "2.0000",
  speechToTextMarkupFactor: "1.0000",
  meetingBotMarkupFactor: "0.0000",
  crmReflectionCostJpyPerFlow: "0.000000",
});
